/**
 * frontend/pages/AuthCallback.jsx — /auth/callback (magic-link landing).
 *
 * Normally just a spinner: Supabase parses the token from the URL (implicit flow,
 * detectSessionInUrl) and useAuth's SIGNED_IN handler navigates to the dashboard / onboarding.
 *
 * Two ways that used to leave the visitor on the spinner forever:
 *   1. The redirect carries an error (#error=access_denied&error_code=otp_expired… — expired link,
 *      link already used, or pre-clicked by a mail scanner such as Outlook Safe Links). Shown at once.
 *   2. Nothing happens. After 15 s with no session, the same card ("Something went wrong…").
 *      If a session does exist by then, go to / and let the router place the user.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BrandMark from '../components/BrandMark';
import { supabase } from '../lib/supabaseClient';
import { bootAuthRedirectError, parseAuthRedirectError } from '../lib/authRedirectError';
import '../AuthScreen.css';

export const CALLBACK_TIMEOUT_MS = 15000;
const EXPIRED = 'That sign-in link has expired or was already used.';
const STUCK = 'Something went wrong signing you in.';

export default function AuthCallback() {
  const navigate = useNavigate();
  const [problem, setProblem] = useState(() => (
    bootAuthRedirectError() || parseAuthRedirectError(window.location) ? EXPIRED : null
  ));

  useEffect(() => {
    if (problem) return undefined;
    const t = setTimeout(async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (data?.session) { navigate('/', { replace: true }); return; }
      } catch { /* fall through to the error card */ }
      setProblem(STUCK);
    }, CALLBACK_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [problem, navigate]);

  if (!problem) {
    return (
      <div className="app-loading">
        <div className="app-loading-logo"><BrandMark /></div>
        <div className="app-loading-spinner" />
        <p className="app-loading-message">Signing you in…</p>
      </div>
    );
  }

  return (
    <div className="auth-screen">
      <div className="auth-card auth-callback-error" role="alert">
        <div className="auth-logo"><BrandMark /></div>
        <h1 className="auth-callback-title">{problem}</h1>
        <button type="button" className="auth-btn-primary auth-btn-block" onClick={() => navigate('/login', { replace: true })}>
          Send a new link
        </button>
        <p className="auth-callback-tip">
          Tip: use the code from the email instead — it works in any browser.
        </p>
      </div>
    </div>
  );
}
